import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useCompany } from '../context/CompanyContext';
import { fyLabel } from '../utils/fy';
import api from '../api';
import './Dashboard.css';

const CATEGORY_LABELS = {
  GST: 'GST Returns',
  TDS: 'TDS / TCS',
  ROC: 'ROC / MCA',
  PF: 'PF',
  ESIC: 'ESIC',
};

function inr(amount) {
  return `₹${Number(amount || 0).toLocaleString('en-IN')}`;
}

function ConsolidatedDashboard() {
  const { fyStartYear, setSelectedCompanyId } = useCompany();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get('/dashboard/consolidated', { params: { fy_year: fyStartYear } })
      .then((res) => {
        setData(res.data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [fyStartYear]);

  const goToCompany = (companyId) => {
    setSelectedCompanyId(companyId);
    navigate('/dashboard', { state: { fromConsolidated: true } });
  };

  return (
    <PageLayout>
      <div className="page-title-row">
        <p className="dash-breadcrumb">Consolidated View</p>
        <h1 className="page-title">Consolidated Dashboard</h1>
        <p className="page-subtitle">All companies — {fyLabel(fyStartYear)}</p>
      </div>

      {error && <p className="dash-error">Backend unreachable: {error}</p>}

      {loading && !data ? (
        <div className="dash-spinner-wrap">
          <div className="dash-spinner" />
          <p>Loading consolidated view...</p>
        </div>
      ) : data ? (
        <div className={loading ? 'dash-refreshing' : ''}>
          <div className="dash-stat-grid">
            <div className="dash-stat-card">
              <div className="dash-stat-value dash-serif">{data.total_companies}</div>
              <div className="dash-stat-label">Total Companies</div>
            </div>
            <div className="dash-stat-card">
              <div className={`dash-stat-value dash-serif ${data.total_overdue > 0 ? 'red' : 'grey'}`}>
                {data.total_overdue}
              </div>
              <div className="dash-stat-label">Total Overdue Items</div>
            </div>
            <div className="dash-stat-card">
              <div className={`dash-stat-value dash-serif ${data.total_penalty_exposure > 0 ? 'red' : 'grey'}`}>
                {inr(data.total_penalty_exposure)}
              </div>
              <div className="dash-stat-label">Total Penalty Exposure</div>
            </div>
            <div className="dash-stat-card">
              <div className="dash-stat-value dash-serif green">{data.overall_compliance_score}%</div>
              <div className="dash-stat-label">Overall Compliance Score</div>
              <div className="dash-stat-sub">Average across companies</div>
            </div>
          </div>

          <div className="dash-card">
            <h2 className="dash-card-title">Company-wise comparison</h2>
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Overdue</th>
                  <th>Filed This Month</th>
                  <th>Penalty Exposure</th>
                  <th>Compliance Score</th>
                  <th>Active Compliances</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.companies.length === 0 && (
                  <tr>
                    <td colSpan={7} className="dash-empty">No companies yet.</td>
                  </tr>
                )}
                {data.companies.map((c) => (
                  <tr
                    key={c.company_id}
                    className="dash-clickable-row"
                    onClick={() => goToCompany(c.company_id)}
                  >
                    <td><strong>{c.company_name}</strong></td>
                    <td className={c.overdue_count > 0 ? 'red' : ''}>{c.overdue_count}</td>
                    <td>{c.filed_this_month}</td>
                    <td className="dash-serif">{inr(c.penalty_exposure)}</td>
                    <td>{c.compliance_score}%</td>
                    <td className="dash-table-sub">{c.active_categories || '—'}</td>
                    <td>
                      <button
                        className="dash-btn dash-btn-orange"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          goToCompany(c.company_id);
                        }}
                      >
                        View Dashboard
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="dash-card">
            <h2 className="dash-card-title">Category-wise consolidated</h2>
            {data.category_breakdown.map((row) => (
              <div className="dash-progress-row" key={row.category}>
                <div className="dash-progress-label">
                  {CATEGORY_LABELS[row.category] || row.category}: {row.pending} pending of {row.total}
                </div>
                <div className="dash-progress-track">
                  <div
                    className={`dash-progress-fill dash-progress-${row.color}`}
                    style={{ width: `${row.total ? Math.round(((row.total - row.pending) / row.total) * 100) : 0}%` }}
                  />
                </div>
                <span className={`dash-pill dash-pill-${row.color === 'green' ? 'green' : row.color === 'amber' ? 'amber' : 'red'}`}>
                  {row.overdue > 0 ? `${row.overdue} overdue` : row.pending > 0 ? `${row.pending} pending` : 'All filed'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </PageLayout>
  );
}

export default ConsolidatedDashboard;
